import React, {useState} from 'react';
import {Card, Input, Button, Alert, Typography, Space, message, Modal} from 'antd';
import {SearchOutlined, AppleOutlined} from '@ant-design/icons';
import {internalApi} from "../../common/axios";
import {buildUrl} from "../../shared/utils/net";

const {Title, Text} = Typography;

const ReceiptFinder = () => {
    const [orderId, setOrderId] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [showWarning, setShowWarning] = useState(false);

    const handleSearch = () => {
        // 주문 ID 입력 검증
        if (!orderId.trim()) {
            message.error('주문 ID를 입력해주세요.');
            return; // 통신하지 않고 함수 종료
        }

        // Apple 영수증 전용 안내 모달 표시
        setShowWarning(true);
    };

    const handleConfirmSearch = async () => {
        setShowWarning(false);

        // 재검증: 주문 ID가 비어있으면 통신하지 않음
        if (!orderId.trim()) {
            message.error('주문 ID를 입력해주세요.');
            return;
        }

        setLoading(true);

        try {
            // API 호출 (실제 구현 예정)
            const res = await internalApi.get(buildUrl('/utility/orderId', {orderId}));
            const user = res.data.user;

            if (user.stateCode < 0) {
                message.error('유효하지 않은 주문 ID입니다.');
                setResult(null);
                return;
            }

            if (user.log.length < 1) {
                message.error(`해당 주문 ID에 대한 영수증 정보를 찾을 수 없습니다. 영수증 아이디: ${user.orderId}`);
                setResult(null);
                return;
            }

            setResult({
                orderId: user.orderId,
                userPid: user.log[0].pid,
                status: user.stateCode === 0 ? 'found' : 'not_found',
                platform: 'Apple App Store'
            });
            message.success('영수증 정보를 찾았습니다.');

        } catch (error) {
            setResult(null);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setOrderId('');
        setResult(null);
    };

    return (
        <div style={{padding: '24px'}}>
            <Card
                title={
                    <Space>
                        <AppleOutlined style={{color: '#1890ff'}}/>
                        <Title level={4} style={{margin: 0}}>영수증 찾기 (Apple)</Title>
                    </Space>
                }
                style={{maxWidth: 800, margin: '0 auto'}}
            >
                {/* 안내 메시지 */}
                <Alert
                    message="Apple App Store 영수증 전용"
                    description="Apple App Store에서 발생한 주문 ID만 조회 가능합니다."
                    type="info"
                    showIcon
                    style={{marginBottom: 24}}
                />

                {/* 주문 ID 입력 */}
                <Space direction="vertical" style={{width: '100%'}} size="large">
                    <div>
                        <Text strong>주문 ID</Text>
                        <Input
                            placeholder="Apple 주문 ID를 입력하세요"
                            value={orderId}
                            onChange={(e) => setOrderId(e.target.value)}
                            onPressEnter={handleSearch}
                            size="large"
                            style={{marginTop: 8}}
                            disabled={loading}
                        />
                    </div>

                    {/* 버튼 영역 */}
                    <Space>
                        <Button
                            type="primary"
                            icon={<SearchOutlined/>}
                            onClick={handleSearch}
                            loading={loading}
                            size="large"
                        >
                            확인
                        </Button>
                        <Button
                            onClick={handleReset}
                            disabled={loading}
                            size="large"
                        >
                            초기화
                        </Button>
                    </Space>
                </Space>

                {/* 결과 표시 */}
                {result && (
                    <Card
                        title="조회 결과"
                        style={{marginTop: 24}}
                        type="inner"
                    >
                        <Space direction="vertical" style={{width: '100%'}}>
                            <div>
                                <Text strong>주문 ID: </Text>
                                <Text code>{result.orderId}</Text>
                            </div>
                            <div>
                                <Text strong>유저 PID: </Text>
                                <Text code style={{color: '#1890ff', fontSize: '16px'}}>
                                    {result.userPid}
                                </Text>
                            </div>
                            {result.platform && (
                                <div>
                                    <Text strong>플랫폼: </Text>
                                    <Text>{result.platform}</Text>
                                </div>
                            )}
                            {result.status && (
                                <div>
                                    <Text strong>상태: </Text>
                                    <Text type={result.status === 'found' ? 'success' : 'warning'}>
                                        {result.status === 'found' ? '조회 성공' : result.status}
                                    </Text>
                                </div>
                            )}
                        </Space>
                    </Card>
                )}
            </Card>

            {/* Apple 영수증 전용 안내 모달 */}
            <Modal
                title="Apple 영수증 조회"
                open={showWarning}
                onOk={handleConfirmSearch}
                onCancel={() => setShowWarning(false)}
                okText="확인"
                cancelText="취소"
                confirmLoading={loading}
            >
                <Alert
                    message="이 기능은 Apple 영수증만 가능합니다."
                    description="Apple App Store에서 발생한 주문 ID만 조회할 수 있습니다. 계속 진행하시겠습니까?"
                    type="warning"
                    showIcon
                />
            </Modal>
        </div>
    );
};

export default ReceiptFinder;